import { useState, useRef, useEffect } from 'react';
import { useTouchPanZoom } from './useTouchPanZoom';
import { getBattleTileSize } from '../utils/battleUtils';

const MIN_ZOOM = 0.3;
const MAX_ZOOM = 2.0;

export function useGridControls() {
  const [gridViewportSize, setGridViewportSize] = useState(() => {
    const saved = localStorage.getItem('battleGridViewportSize');
    return saved ? JSON.parse(saved) : { width: 800, height: 600 };
  });
  const [zoomLevel, setZoomLevel] = useState(() => {
    const saved = localStorage.getItem('battleGridZoom');
    return saved ? parseFloat(saved) : 1.0;
  });
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeEdge, setResizeEdge] = useState<string | null>(null);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const gridContainerRef = useRef<HTMLDivElement>(null);

  // Persist viewport size
  useEffect(() => {
    localStorage.setItem('battleGridViewportSize', JSON.stringify(gridViewportSize));
  }, [gridViewportSize]);

  // Persist zoom level
  useEffect(() => {
    localStorage.setItem('battleGridZoom', zoomLevel.toString());
  }, [zoomLevel]);

  // Resize effect — attaches/detaches mousemove + mouseup listeners
  useEffect(() => {
    if (!isResizing) return;

    const handleResizeMove = (e: MouseEvent) => {
      const deltaX = e.clientX - resizeStart.x;
      const deltaY = e.clientY - resizeStart.y;

      let newWidth = resizeStart.width;
      let newHeight = resizeStart.height;

      if (resizeEdge?.includes('right')) newWidth = Math.max(400, resizeStart.width + deltaX);
      if (resizeEdge?.includes('left'))  newWidth = Math.max(400, resizeStart.width - deltaX);
      if (resizeEdge?.includes('bottom')) newHeight = Math.max(300, resizeStart.height + deltaY);
      if (resizeEdge?.includes('top'))   newHeight = Math.max(300, resizeStart.height - deltaY);

      setGridViewportSize({ width: newWidth, height: newHeight });
    };

    const handleResizeEnd = () => {
      setIsResizing(false);
      setResizeEdge(null);
    };

    window.addEventListener('mousemove', handleResizeMove);
    window.addEventListener('mouseup', handleResizeEnd);
    return () => {
      window.removeEventListener('mousemove', handleResizeMove);
      window.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [isResizing, resizeEdge, resizeStart]);

  const autoFit = (gridCols: number, gridRows: number) => {
    if (!gridContainerRef.current) return;
    const { clientWidth: w, clientHeight: h } = gridContainerRef.current;
    if (w === 0 || h === 0) return;
    const tileSize = getBattleTileSize();
    const zoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, Math.min(w / (gridCols * tileSize), h / (gridRows * tileSize)) * 0.92));
    setZoomLevel(zoom);
    setPanOffset({ x: 0, y: 0 });
  };

  const handleZoomIn = () => setZoomLevel(prev => Math.min(prev + 0.1, MAX_ZOOM));
  const handleZoomOut = () => setZoomLevel(prev => Math.max(prev - 0.1, MIN_ZOOM));
  const handleResetZoom = (gridCols?: number, gridRows?: number) => {
    if (gridCols && gridRows) {
      autoFit(gridCols, gridRows);
    } else {
      setZoomLevel(1.0);
      setPanOffset({ x: 0, y: 0 });
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.target === gridContainerRef.current || (e.target as HTMLElement).classList.contains('battle-grid')) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      setPanOffset({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
    }
  };

  const handleMouseUp = () => setIsDragging(false);
  const handleMouseLeave = () => setIsDragging(false);

  const handleResizeStart = (e: React.MouseEvent, edge: string) => {
    e.stopPropagation();
    e.preventDefault();
    setIsResizing(true);
    setResizeEdge(edge);
    setResizeStart({ x: e.clientX, y: e.clientY, width: gridViewportSize.width, height: gridViewportSize.height });
  };

  // Touch: one-finger drag pans, two-finger pinch zooms, a clean tap falls through to the
  // browser's native click synthesis so tile selection keeps working untouched (GH-54).
  const { onTouchStart, onTouchMove, onTouchEnd } = useTouchPanZoom({
    panOffset, setPanOffset, zoom: zoomLevel, setZoom: setZoomLevel, minZoom: MIN_ZOOM, maxZoom: MAX_ZOOM,
  });

  return {
    gridViewportSize,
    zoomLevel,
    panOffset,
    setPanOffset,
    isDragging,
    isResizing,
    gridContainerRef,
    autoFit,
    handleZoomIn,
    handleZoomOut,
    handleResetZoom,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
    handleResizeStart,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
  };
}
